import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AutoAwesome as ExtractIcon,
  TableChart as SheetIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Add as AddIcon,
  MailOutline as MailIcon,
} from "@mui/icons-material";
import ResizablePanel from "../../components/common/ResizablePanel";
import { useLayout } from "../../contexts/LayoutContext";
import { useUploadContext } from "../../Provider/UploadContext";
import AddressbookPanelShell from "../addressbook/panels/AddressbookPanelShell";
import FilesPanel from "../addressbook/panels/FilesPanel";
import { myToast } from "../../libs";
import { LOAN_INBOX_ADDRESS, SAMPLE_LOAN_EMAILS } from "./sampleLoanEmails";
import { LOAN_FIELDS, extractLoanFields, toSpreadsheetRows, rowsToCsv, rowsToTsv } from "./loanEmailExtract";

/*
 * Loan Inbox
 * ----------
 * Deals arrive as emails. The inbox groups them by sender; selecting a person
 * shows their whole thread and one merged loan record: every email is read,
 * and the newest email wins per field. The mailbox (loans@oelement.ai) is a
 * UI mock, "Send to lender" is the wiring point for a real integration, and
 * the Files panel is the shared one from Contacts, keyed to the sender.
 * Extraction runs in the browser (see loanEmailExtract.js).
 */

const INDIGO_900 = "#1A237E";
const INDIGO_500 = "#3F51B5";
const INK = "#1A1F36";
const MUTED = "#5A6182";
const TINT = "#F4F5FB";
const LINE = "#E8EAF6";

const GROUPS = ["Contact", "Property", "Loan", "Financials", "Timing"];

// Columns of the Loans table: sender, then the fields a lender asks about first.
const LOAN_TABLE_COLUMNS = [
  { key: "sender", label: "From" },
  ...["borrower", "propertyAddress", "propertyType", "purpose", "loanAmount", "ltv", "dscr", "closeBy"]
    .map((key) => ({ key, label: LOAN_FIELDS.find((f) => f.key === key)?.label || key })),
];

const senderKey = (e) => String(e.fromEmail || e.fromName || "").toLowerCase();
const shortDate = (receivedAt) => String(receivedAt || "").slice(5, 10).replace("-", "/");

// Read every email of a thread, oldest to newest; a later email overrides a
// field only when it actually states it. Each field remembers which email it
// came from.
const extractThread = (emailsNewestFirst) => {
  const ordered = [...emailsNewestFirst].reverse();
  const fields = {};
  LOAN_FIELDS.forEach(({ key }) => { fields[key] = { value: "", found: false, source: null }; });
  ordered.forEach((email) => {
    const r = extractLoanFields(email.body, { fromEmail: email.fromEmail, fromName: email.fromName });
    LOAN_FIELDS.forEach(({ key }) => {
      const cell = r.fields[key];
      if (cell?.value) fields[key] = { value: cell.value, found: true, source: email.receivedAt };
    });
  });
  const missing = LOAN_FIELDS.filter((f) => !fields[f.key].value).map((f) => f.key);
  return { fields, missing };
};

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

// One email inside a sender group: plain white card, indented under the
// sender band, with a mail glyph so it never reads as a person.
const EmailRow = ({ email, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      gap: 0.75,
      p: 0.75,
      pl: 1,
      mt: 0.5,
      ml: 2.5,
      borderRadius: 1,
      cursor: "pointer",
      border: `1px solid ${LINE}`,
      backgroundColor: "#FFFFFF",
      "&:hover": { borderColor: INDIGO_500, backgroundColor: TINT },
    }}
  >
    <MailIcon sx={{ fontSize: 15, color: "#9FA8DA", mt: 0.25 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {email.subject}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: MUTED, whiteSpace: "nowrap" }}>{email.receivedAt}</Typography>
      </Box>
      <Typography sx={{ fontSize: 11, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {email.body.replace(/\s+/g, " ").slice(0, 90)}
      </Typography>
    </Box>
  </Box>
);

const PageLoanInbox = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isPanelOpen, togglePanel } = useLayout();
  const { uploadSingle } = useUploadContext() || {};
  const userDomain = useSelector((state) => state.auth.user?.domain || "");
  const domain = userDomain && userDomain !== "All" ? userDomain : "";

  const [emails, setEmails] = useState(SAMPLE_LOAN_EMAILS);
  const [selectedKey, setSelectedKey] = useState(senderKey(SAMPLE_LOAN_EMAILS[0]));
  // { [senderKey]: { fields, missing } } merged per thread, editable.
  const [extractions, setExtractions] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteFrom, setPasteFrom] = useState("");
  const [pasteSubject, setPasteSubject] = useState("");
  const [pasteBody, setPasteBody] = useState("");
  const [sentOpen, setSentOpen] = useState(false);

  // Files panel state (same wiring as Contacts).
  const [filesDisplayName, setFilesDisplayName] = useState("");
  const [filesSelectedType, setFilesSelectedType] = useState("");
  const [filesSelectedFile, setFilesSelectedFile] = useState(null);
  const [filesStatus, setFilesStatus] = useState("");
  const [filesProgress, setFilesProgress] = useState(0);

  // Inbox grouped by sender: newest email first in each group, groups ordered
  // by their newest email.
  const groups = useMemo(() => {
    const byKey = new Map();
    emails.forEach((e) => {
      const key = senderKey(e);
      if (!byKey.has(key)) byKey.set(key, { key, fromName: e.fromName, fromEmail: e.fromEmail, emails: [] });
      byKey.get(key).emails.push(e);
    });
    const list = [...byKey.values()];
    list.forEach((g) => g.emails.sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1)));
    list.sort((a, b) => (a.emails[0].receivedAt < b.emails[0].receivedAt ? 1 : -1));
    return list;
  }, [emails]);

  const selected = useMemo(() => groups.find((g) => g.key === selectedKey) || null, [groups, selectedKey]);
  const extraction = selected ? extractions[selected.key] : null;
  const threadSignature = selected ? selected.emails.map((e) => e.id).join("|") : "";

  const runExtract = (group, { quiet = false } = {}) => {
    if (!group) return;
    const result = extractThread(group.emails);
    setExtractions((prev) => ({ ...prev, [group.key]: result }));
    if (!quiet) {
      const found = LOAN_FIELDS.length - result.missing.length;
      myToast.success(`Read ${found} of ${LOAN_FIELDS.length} fields from ${group.emails.length} ${group.emails.length === 1 ? "email" : "emails"}`);
    }
  };

  // Selecting a person (or a new email arriving in their thread) re-reads the
  // thread. "Read the deal" re-reads on demand and resets edits.
  useEffect(() => {
    if (selected) runExtract(selected, { quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.key, threadSignature]);

  const setFieldValue = (key, value) => {
    if (!selected) return;
    setExtractions((prev) => {
      const cur = prev[selected.key] || { fields: {}, missing: [] };
      const fields = { ...cur.fields, [key]: { ...(cur.fields?.[key] || { found: false, source: null }), value, edited: true } };
      const missing = LOAN_FIELDS.filter((f) => !String(fields[f.key]?.value || "").trim()).map((f) => f.key);
      return { ...prev, [selected.key]: { fields, missing } };
    });
  };

  const extractedList = useMemo(
    () => groups
      .filter((g) => extractions[g.key])
      .map((g) => ({
        receivedAt: g.emails[0].receivedAt,
        fromEmail: g.fromEmail,
        subject: g.emails[0].subject,
        fields: extractions[g.key].fields,
      })),
    [groups, extractions],
  );

  // Every email across senders, newest first (the Emails panel).
  const allEmails = useMemo(
    () => [...emails].sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1)),
    [emails],
  );

  // One loan per sender for every thread, read on the fly when a thread has
  // not been opened yet (the Loans panel).
  const allLoans = useMemo(
    () => groups.map((g) => ({
      key: g.key,
      fromName: g.fromName,
      fromEmail: g.fromEmail,
      receivedAt: g.emails[0].receivedAt,
      emailCount: g.emails.length,
      fields: (extractions[g.key] || extractThread(g.emails)).fields,
    })),
    [groups, extractions],
  );

  const downloadCsv = () => {
    if (!extractedList.length) return;
    const csv = rowsToCsv(toSpreadsheetRows(extractedList));
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `loan-inbox-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyTsv = async () => {
    if (!extractedList.length) return;
    try {
      await navigator.clipboard.writeText(rowsToTsv(toSpreadsheetRows(extractedList)));
      myToast.success(`Copied ${extractedList.length} ${extractedList.length === 1 ? "loan" : "loans"}. Paste into Google Sheets or Excel.`);
    } catch {
      myToast.error("Clipboard is not available in this browser");
    }
  };

  const addPasted = () => {
    const body = pasteBody.trim();
    if (!body) return;
    const emailMatch = pasteFrom.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    const email = {
      id: `p-${Date.now()}`,
      fromName: pasteFrom.replace(/<.*>/, "").trim() || (emailMatch ? emailMatch[0] : "Pasted email"),
      fromEmail: emailMatch ? emailMatch[0] : "",
      subject: pasteSubject.trim() || "(no subject)",
      receivedAt: new Date().toISOString().slice(0, 16).replace("T", " "),
      body,
    };
    setEmails((prev) => [email, ...prev]);
    setSelectedKey(senderKey(email));
    setPasteOpen(false);
    setPasteFrom("");
    setPasteSubject("");
    setPasteBody("");
  };

  const foundCount = extraction ? LOAN_FIELDS.length - extraction.missing.length : 0;

  // Files are attached to the sender (the borrower or referring broker), not
  // to one email, so the whole thread shares them.
  const filesEntity = selected
    ? { id: `loan-${selected.key.replace(/[^a-z0-9]+/g, "-")}`, domain, details: { files: [] } }
    : { id: "", details: { files: [] } };

  return (
    <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: "100%", minHeight: 0, minWidth: 0, overflow: "hidden", gap: 1 }}>
      {isPanelOpen("loanInbox") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 360}
        minWidth={isMobile ? window.innerWidth : 280}
        maxWidth={isMobile ? window.innerWidth : 560}
        fillRemaining={false}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <AddressbookPanelShell
          onClose={() => togglePanel("loanInbox")}
          collectionLabel="Loan Inbox"
          titleContent={(
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>Loan Inbox</Typography>
              <Chip size="small" label={LOAN_INBOX_ADDRESS} sx={{ height: 20, fontSize: 11, backgroundColor: TINT, color: INDIGO_900, fontWeight: 600 }} />
            </Box>
          )}
          headerActions={(
            <Tooltip title="Paste an email">
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setPasteOpen(true)} sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 600, color: INDIGO_500 }}>
                Paste
              </Button>
            </Tooltip>
          )}
          footerLeft={<Typography sx={{ fontSize: 11, color: "text.secondary" }}>{emails.length} emails from {groups.length} people</Typography>}
        >
          {groups.map((g) => {
            const isSelected = g.key === selectedKey;
            const isCollapsed = Boolean(collapsed[g.key]);
            return (
              <Box key={g.key} sx={{ mb: 1.25, pb: 0.5, borderLeft: `3px solid ${isSelected ? INDIGO_500 : "#C5CAE9"}`, pl: 0.75 }}>
                <Box
                  onClick={() => setSelectedKey(g.key)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    backgroundColor: isSelected ? INDIGO_900 : "#E8EAF6",
                    color: isSelected ? "#FFFFFF" : INDIGO_900,
                    "&:hover": { backgroundColor: isSelected ? INDIGO_900 : "#C5CAE9" },
                  }}
                >
                  <Box sx={{ width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, backgroundColor: isSelected ? "#3F51B5" : "#FFFFFF", color: isSelected ? "#FFFFFF" : INDIGO_900, flexShrink: 0 }}>
                    {initials(g.fromName)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "inherit", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.fromName}</Typography>
                    <Typography sx={{ fontSize: 11, color: "inherit", opacity: 0.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.fromEmail}</Typography>
                  </Box>
                  <Chip size="small" label={`${g.emails.length} ${g.emails.length === 1 ? "email" : "emails"}`} sx={{ height: 18, fontSize: 10.5, fontWeight: 700, backgroundColor: isSelected ? "#3F51B5" : "#FFFFFF", color: isSelected ? "#FFFFFF" : INDIGO_900 }} />
                  <Typography
                    onClick={(e) => { e.stopPropagation(); setCollapsed((prev) => ({ ...prev, [g.key]: !prev[g.key] })); }}
                    sx={{ fontSize: 15, color: "inherit", width: 18, textAlign: "center", cursor: "pointer" }}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                  >
                    {isCollapsed ? "+" : "–"}
                  </Typography>
                </Box>
                {isCollapsed ? null : g.emails.map((e) => (
                  <EmailRow key={e.id} email={e} onClick={() => setSelectedKey(g.key)} />
                ))}
              </Box>
            );
          })}
        </AddressbookPanelShell>
      </ResizablePanel>
      )}

      {isPanelOpen("loanEmail") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 440}
        minWidth={isMobile ? window.innerWidth : 300}
        maxWidth={isMobile ? window.innerWidth : 820}
        fillRemaining={false}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <AddressbookPanelShell
          onClose={() => togglePanel("loanEmail")}
          collectionLabel="Emails"
          titleContent={(
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {selected ? `${selected.fromName} (${selected.emails.length})` : "Emails"}
            </Typography>
          )}
          headerActions={selected ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<ExtractIcon sx={{ fontSize: 14 }} />}
              onClick={() => runExtract(selected)}
              sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 600, backgroundColor: INDIGO_500, "&:hover": { backgroundColor: "#3949AB" }, boxShadow: "none", whiteSpace: "nowrap" }}
            >
              Read the deal
            </Button>
          ) : null}
        >
          {!selected ? (
            <Typography sx={{ textAlign: "center", py: 4, fontSize: 13, color: "text.secondary" }}>Select a person.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {selected.emails.map((email, i) => (
                <Box key={email.id} sx={{ p: 1.25, borderRadius: 1.5, border: `1px solid ${i === 0 ? INDIGO_500 : LINE}`, backgroundColor: "#FFFFFF" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email.subject}</Typography>
                    {i === 0 ? <Chip size="small" label="Latest" sx={{ height: 18, fontSize: 10.5, fontWeight: 700, backgroundColor: "#E8EAF6", color: INDIGO_900 }} /> : null}
                    <Typography sx={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>{email.receivedAt}</Typography>
                  </Box>
                  <Typography component="pre" sx={{ m: 0, fontFamily: "inherit", fontSize: 12.5, color: INK, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {email.body}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </AddressbookPanelShell>
      </ResizablePanel>
      )}

      {isPanelOpen("loanFields") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 480}
        minWidth={isMobile ? window.innerWidth : 320}
        maxWidth={isMobile ? window.innerWidth : 900}
        fillRemaining={!isPanelOpen("loanFiles") && !isPanelOpen("loanEmails") && !isPanelOpen("loanLoans")}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <AddressbookPanelShell
          onClose={() => togglePanel("loanFields")}
          collectionLabel="Extracted loan"
          titleContent={(
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {selected ? `Loan: ${selected.fromName}` : "Extracted loan"}
            </Typography>
          )}
          headerActions={selected ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Tooltip title="Copy all extracted loans for Google Sheets or Excel">
                <span>
                  <Button size="small" startIcon={<CopyIcon sx={{ fontSize: 14 }} />} disabled={!extractedList.length} onClick={copyTsv} sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 600, color: INDIGO_500 }}>
                    Copy
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Download all extracted loans as a spreadsheet">
                <span>
                  <Button size="small" startIcon={<SheetIcon sx={{ fontSize: 14 }} />} disabled={!extractedList.length} onClick={downloadCsv} sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 600, color: INDIGO_500 }}>
                    Spreadsheet
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Send this loan to the lender portal">
                <span>
                  <Button size="small" startIcon={<SendIcon sx={{ fontSize: 14 }} />} disabled={!extraction} onClick={() => setSentOpen(true)} sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 600, color: INDIGO_500 }}>
                    Send to lender
                  </Button>
                </span>
              </Tooltip>
            </Box>
          ) : null}
          footerLeft={extraction && selected ? (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {foundCount} of {LOAN_FIELDS.length} fields from {selected.emails.length} {selected.emails.length === 1 ? "email" : "emails"}{extraction.missing.length ? `, ${extraction.missing.length} to fill in` : ""}
            </Typography>
          ) : null}
        >
          {!selected || !extraction ? (
            <Typography sx={{ textAlign: "center", py: 4, fontSize: 13, color: "text.secondary" }}>Select a person.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {GROUPS.map((group) => (
                <Box key={group} sx={{ p: 1.25, borderRadius: 1.5, border: `1px solid ${LINE}`, backgroundColor: "#FFFFFF" }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>{group}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 0.75 }}>
                    {LOAN_FIELDS.filter((f) => f.group === group).map((f) => {
                      const cell = extraction.fields[f.key] || { value: "", found: false, source: null };
                      const isLatest = cell.source && selected.emails[0] && cell.source === selected.emails[0].receivedAt;
                      const label = cell.source && selected.emails.length > 1 ? `${f.label} (${shortDate(cell.source)})` : f.label;
                      return (
                        <Tooltip key={f.key} title={cell.source ? `From the ${cell.source} email` : ""} placement="top">
                          <TextField
                            label={label}
                            size="small"
                            value={cell.value}
                            onChange={(e) => setFieldValue(f.key, e.target.value)}
                            placeholder="not in email"
                            InputProps={{ sx: { fontSize: 12.5, backgroundColor: cell.value ? (cell.found ? (isLatest && selected.emails.length > 1 ? "#E3F2FD" : "#F1F8E9") : "#FFFFFF") : "#FFF8E1" } }}
                            InputLabelProps={{ sx: { fontSize: 12 } }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </AddressbookPanelShell>
      </ResizablePanel>
      )}

      {isPanelOpen("loanFiles") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 360}
        minWidth={isMobile ? window.innerWidth : 280}
        maxWidth={isMobile ? window.innerWidth : 700}
        fillRemaining={!isPanelOpen("loanEmails") && !isPanelOpen("loanLoans")}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <FilesPanel
          contactInfo={filesEntity}
          updateContactInfo={() => {}}
          uploadSingle={uploadSingle}
          actionSave={() => {}}
          filesDisplayName={filesDisplayName}
          setFilesDisplayName={setFilesDisplayName}
          filesSelectedType={filesSelectedType}
          setFilesSelectedType={setFilesSelectedType}
          filesSelectedFile={filesSelectedFile}
          setFilesSelectedFile={setFilesSelectedFile}
          filesStatus={filesStatus}
          setFilesStatus={setFilesStatus}
          filesProgress={filesProgress}
          setFilesProgress={setFilesProgress}
          onClose={() => togglePanel("loanFiles")}
          fileSettingsType="contact"
          fileRefCollection="loanInbox"
          fileRefSchemaType="loan"
          fileRefName="loanInbox"
          emptySelectionMessage="Select a person first"
          previewEntityType="contact"
          entityName={selected?.fromName || ""}
        />
      </ResizablePanel>
      )}

      {isPanelOpen("loanEmails") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 380}
        minWidth={isMobile ? window.innerWidth : 280}
        maxWidth={isMobile ? window.innerWidth : 700}
        fillRemaining={!isPanelOpen("loanLoans")}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <AddressbookPanelShell
          onClose={() => togglePanel("loanEmails")}
          collectionLabel="Emails"
          titleContent={<Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>Emails</Typography>}
          footerLeft={<Typography sx={{ fontSize: 11, color: "text.secondary" }}>{allEmails.length} emails</Typography>}
        >
          {allEmails.map((email) => {
            const isSelected = senderKey(email) === selectedKey;
            return (
              <Box
                key={email.id}
                onClick={() => setSelectedKey(senderKey(email))}
                sx={{ p: 1, mb: 0.5, borderRadius: 1.5, cursor: "pointer", border: `1px solid ${isSelected ? INDIGO_500 : LINE}`, backgroundColor: isSelected ? "#E8EAF6" : "#FFFFFF", "&:hover": { borderColor: INDIGO_500 } }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email.fromName}</Typography>
                  <Typography sx={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>{email.receivedAt}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: INDIGO_900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email.subject}</Typography>
                <Typography sx={{ fontSize: 11, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {email.body.replace(/\s+/g, " ").slice(0, 90)}
                </Typography>
              </Box>
            );
          })}
        </AddressbookPanelShell>
      </ResizablePanel>
      )}

      {isPanelOpen("loanLoans") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 520}
        minWidth={isMobile ? window.innerWidth : 320}
        maxWidth={isMobile ? window.innerWidth : 1100}
        fillRemaining
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <AddressbookPanelShell
          onClose={() => togglePanel("loanLoans")}
          collectionLabel="Loans"
          titleContent={<Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>Loans</Typography>}
          footerLeft={<Typography sx={{ fontSize: 11, color: "text.secondary" }}>{allLoans.length} loans</Typography>}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 720, fontSize: 12, color: INK }}>
              <Box component="thead">
                <Box component="tr">
                  {LOAN_TABLE_COLUMNS.map((c) => (
                    <Box key={c.key} component="th" sx={{ textAlign: "left", p: 0.75, fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: `1px solid ${LINE}`, whiteSpace: "nowrap", position: "sticky", top: 0, backgroundColor: "#FFFFFF" }}>
                      {c.label}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {allLoans.map((loan) => {
                  const isSelected = loan.key === selectedKey;
                  return (
                    <Box
                      key={loan.key}
                      component="tr"
                      onClick={() => setSelectedKey(loan.key)}
                      sx={{ cursor: "pointer", backgroundColor: isSelected ? "#E8EAF6" : "transparent", "&:hover": { backgroundColor: isSelected ? "#E8EAF6" : TINT } }}
                    >
                      {LOAN_TABLE_COLUMNS.map((c) => {
                        const cell = c.key === "sender" ? { value: loan.fromName, found: true } : (loan.fields[c.key] || { value: "", found: false });
                        return (
                          <Box key={c.key} component="td" sx={{ p: 0.75, borderBottom: `1px solid ${LINE}`, whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", color: cell.value ? INK : "#B08A00", fontWeight: c.key === "sender" ? 700 : 400 }}>
                            {cell.value || "missing"}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </AddressbookPanelShell>
      </ResizablePanel>
      )}

      <Dialog open={pasteOpen} onClose={() => setPasteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Paste an email</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1, pt: "8px !important" }}>
          <TextField label="From" size="small" value={pasteFrom} onChange={(e) => setPasteFrom(e.target.value)} placeholder="Name <name@company.com>" />
          <TextField label="Subject" size="small" value={pasteSubject} onChange={(e) => setPasteSubject(e.target.value)} />
          <TextField label="Email text" size="small" multiline minRows={8} value={pasteBody} onChange={(e) => setPasteBody(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setPasteOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button size="small" variant="contained" disabled={!pasteBody.trim()} onClick={addPasted} sx={{ textTransform: "none", backgroundColor: INDIGO_500 }}>
            Add and read
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sentOpen} onClose={() => setSentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Send to lender portal</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: INK }}>
            This is where the loan goes to the lender's underwriting portal, a CRM, or a shared spreadsheet. The connection is set up per lender once we know which system they use.
          </Typography>
          {extraction ? (
            <Box sx={{ mt: 1, p: 1, borderRadius: 1, backgroundColor: TINT, border: `1px solid ${LINE}` }}>
              {["borrower", "propertyAddress", "purpose", "loanAmount", "closeBy"].map((k) => (
                <Typography key={k} sx={{ fontSize: 12, color: INK }}>
                  <Box component="span" sx={{ color: MUTED }}>{LOAN_FIELDS.find((f) => f.key === k)?.label}: </Box>
                  {extraction.fields[k]?.value || "not in email"}
                </Typography>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSentOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PageLoanInbox;
